export function useFacilityWizardGuard() {
  const { t } = useFormatOptions();
  const appStore = useAppStore();
  const facilityStore = useFacilityStore();
  const setupWizardStore = useSetupWizardStore();
  const { isLoadingCurrentFacility } = useFacilityApi();
  const {
    INTRO_STEP_INDEX,
    INFO_STEP_INDEX,
    rootRoute,
    makeGuardResult,
    resolveFacilityRouteContext,
    createFacilityPath,
  } = useFacilityRoute();

  const guardFeatureRoute: FeatureGuard = (route) => {
    const routeContext = resolveFacilityRouteContext(route);

    if (!routeContext.isRecognized) {
      devConsole.warn(
        `🚦 guard rule triggered: unrecognized route name for ${route.path}`,
        routeContext,
      );
      return makeGuardResult(route, "root");
    }

    if (!routeContext.isFacilityWizard)
      return makeGuardResult(route, "continue");

    if (routeContext.invalidFacilityId) {
      devConsole.error("🚦 guard rule triggered: unrecognized facilityId");
      return makeGuardResult(route, "abort");
    }

    if (isLoadingCurrentFacility.value) {
      devConsole.info(
        "🚦 guard rule triggered: Waiting for current facility to load",
      );
      return makeGuardResult(route, "continue");
    }

    if (!setupWizardStore.formData.facility) {
      devConsole.info("🚦 guard rule triggered: Initializing wizard form");
      setupWizardStore.init(facilityStore.currentFacility);
    }

    const shouldSkipIntroduction =
      setupWizardStore.skipIntroduction || routeContext.params.facilityId;

    if (
      setupWizardStore.facilityFormData?.id === undefined &&
      shouldSkipIntroduction &&
      routeContext.facilityWizardStepIndex === INTRO_STEP_INDEX
    ) {
      devConsole.info(
        "🚦 guard rule triggered: Redirecting to the first step of the setup when accessing a facility with no id",
      );

      return makeGuardResult(route, {
        replace: createFacilityPath({
          wizardStep: INFO_STEP_INDEX,
        }),
      });
    }

    const { latestCompletedStep, latestAccessibleStep } =
      calculateFacilityLatestStep(setupWizardStore.facilityFormData);

    // The rest of validations is for facilities with id
    if (routeContext.matches.setupFlow === "strict") {
      if (
        setupWizardStore.facilityFormData.setupStatus &&
        setupWizardStore.facilityFormData.setupStatus !== "new"
      ) {
        devConsole.info(
          "🚦 guard rule triggered: EXISTING facilities got to first step when accessing /setup",
        );

        return makeGuardResult(route, {
          redirect: createFacilityPath({
            wizardStep: latestCompletedStep.index,
            facilityId: setupWizardStore.facilityFormData.id,
          }),
        });
      }

      devConsole.info(
        "🚦 guard rule triggered: NEW Facilities go to latest completed step when accessing /setup",
      );

      return makeGuardResult(route, {
        redirect: createFacilityPath({
          wizardStep: latestCompletedStep.index,
          facilityId: setupWizardStore.facilityFormData.id,
        }),
      });
    }

    if (
      routeContext.facilityWizardStepIndex === undefined ||
      routeContext.facilityWizardStepIndex > latestAccessibleStep.index
    ) {
      devConsole.warn(
        `🚦 guard rule triggered: Redirecting to the latest accessible step ${latestAccessibleStep.index} of the setup for existing facilities`,
      );

      appStore.addWarningMessage(t("cksetup.wizard.completePreviousSteps"));

      return makeGuardResult(route, {
        replace: createFacilityPath({
          wizardStep: latestAccessibleStep.index,
          facilityId: setupWizardStore.facilityFormData.id,
        }),
      });
    }

    return makeGuardResult(route, "continue");
  };

  /**
   * Guard the route when leaving the facility wizard flow
   * @param from
   * @param to
   * @returns false if the user should be prompted to confirm unsaved changes
   */
  const guardExitRoute: ExitGuard = ({ from, to }) => {
    const fromRouteContext = resolveFacilityRouteContext(from);
    const toRouteContext = resolveFacilityRouteContext(to);

    // Cleanup (leaving routes) case scenarios
    if (!fromRouteContext.isRecognized) return makeGuardResult(to, "continue");

    const switchedFacilityId =
      appStore.activeFacilityId?.toString() !==
        to.params.facilityId?.toString() && // if is just reloading the current facility after creation
      from.params.facilityId !== to.params.facilityId;

    const isLeavingWizard =
      fromRouteContext.isFacilityWizard &&
      (!toRouteContext.isFacilityWizard || switchedFacilityId);

    const isLeavingWizardStep =
      isLeavingWizard ||
      fromRouteContext.facilityWizardStepIndex !==
        toRouteContext.facilityWizardStepIndex;

    if (isLeavingWizardStep) {
      if (
        setupWizardStore.confirmUnsavedChangesBeforeLeaving(
          toRouteContext.localizedPath,
        )
      ) {
        devConsole.warn(
          "🚦 middleware rule triggered: there are wizard unsaved changes, showing a confirmation dialog",
        );
        return makeGuardResult(to, "abort");
      }
    }

    if (isLeavingWizard) {
      devConsole.info(
        "🚦 middleware rule triggered: resetting setup wizard store as user is leaving the wizard",
      );
      setupWizardStore.reset();
    }

    return makeGuardResult(to, "continue");
  };

  return {
    name: "facilityWizardGuard",
    rootRoute,
    guardFeatureRoute,
    guardExitRoute,
  };
}
